// FR-AD4/F13b: policy CRUD (org, team or folder scope — folders come from F2's
// `GET /orgs/{org_id}/folders`, and FR-AD4's "space" override maps onto them) plus the
// dry-run preview and the admin-visible 7-day delete grace list (S2's
// `GET /orgs/{org_id}/retention-policies/pending`).
import { useCallback, useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Badge, Card, Select } from "@grackle/ui";
import ConfirmDialog from "../components/ConfirmDialog";
import { api, ApiFail } from "../api/client";
import { listFolders } from "../library/api";
import type {
  FolderOut,
  ListResponse,
  PendingPurgeOut,
  RetentionAction,
  RetentionAppliesTo,
  RetentionDryRunOut,
  RetentionPolicyOut,
  RetentionScope,
  TeamOut,
} from "../api/types";
import { Button, Field, HoldBadge, Notice } from "../components/ui";
import { SettingsSection, SaveRow } from "./SettingsSection";
import type { AdminOutletContext } from "./AdminLayout";

function scopeLabel(policy: RetentionPolicyOut, teams: TeamOut[], folders: FolderOut[]): string {
  if (policy.scope === "org") return "Entire organization";
  if (policy.scope === "team") {
    const team = teams.find((t) => t.id === policy.scope_id);
    return team ? `Team · ${team.name}` : "Team (deleted)";
  }
  const folder = folders.find((f) => f.id === policy.scope_id);
  return folder ? `Folder · ${folder.name}` : "Folder (deleted)";
}

function appliesToLabel(v: RetentionAppliesTo): string {
  return v === "all" ? "Everything" : v === "meetings" ? "Meeting recordings only" : "Non-meeting recordings only";
}

/** Exported for `RetentionPage.test.ts` (W7): the "delete" action's copy must explain the
 * 7-day trash-then-purge grace, not just say "delete". */
export function actionCopy(action: RetentionAction): string {
  return action === "trash"
    ? "Move to trash after the window — owners can still restore it until the normal trash purge runs."
    : "Move to trash and permanently delete 7 days later. Owners are notified 7 days before the action.";
}

/** Exported for `RetentionPage.test.ts` (W7): the dry-run preview's count copy, pulled out of
 * JSX so it's testable without mounting the page. */
export function dryRunSummaryText(dryRun: RetentionDryRunOut | null): string {
  if (!dryRun) return "Preview not loaded yet.";
  return `Next sweep would affect ${dryRun.count} recording${dryRun.count === 1 ? "" : "s"} under the policies below.`;
}

interface PolicyFormState {
  scope: RetentionScope;
  scope_id: string;
  action: RetentionAction;
  after_days: string;
  applies_to: RetentionAppliesTo;
}

const emptyForm: PolicyFormState = {
  scope: "org",
  scope_id: "",
  action: "trash",
  after_days: "30",
  applies_to: "all",
};

function PolicyForm({
  teams,
  folders,
  initial,
  isEdit,
  submitting,
  onCancel,
  onSubmit,
}: {
  teams: TeamOut[];
  folders: FolderOut[];
  initial: PolicyFormState;
  /** Scope/scope_id are immutable once created (the server refuses to change them too) — the
   * scope controls are disabled rather than hidden so an edit still shows what it applies to. */
  isEdit: boolean;
  submitting: boolean;
  onCancel: () => void;
  onSubmit: (form: PolicyFormState) => void;
}) {
  const [form, setForm] = useState(initial);

  return (
    <SettingsSection
      title={isEdit ? "Edit retention policy" : "New retention policy"}
      description={isEdit ? "What it applies to is fixed; the action, window and kind can change." : "Pick what it applies to, then what happens and when."}
    >
      <div className="admin-field-grid">
      <Select
        label="Applies to"
        value={form.scope}
        disabled={isEdit}
        onChange={(v) => setForm((f) => ({ ...f, scope: v as RetentionScope, scope_id: "" }))}
        options={[
          { value: "org", label: "Entire organization" },
          { value: "team", label: "A team" },
          { value: "folder", label: "A folder" },
        ]}
      />
      {form.scope === "team" ? (
        <Select
          label="Team"
          placeholder="Select a team…"
          value={form.scope_id}
          disabled={isEdit}
          onChange={(v) => setForm((f) => ({ ...f, scope_id: v }))}
          options={teams.map((t) => ({ value: t.id, label: t.name }))}
        />
      ) : null}
      {form.scope === "folder" ? (
        <Select
          label="Folder"
          placeholder="Select a folder…"
          value={form.scope_id}
          disabled={isEdit}
          onChange={(v) => setForm((f) => ({ ...f, scope_id: v }))}
          options={folders.filter((f) => f.scope !== "personal").map((f) => ({ value: f.id, label: f.name }))}
        />
      ) : null}
      <Select
        label="Action"
        value={form.action}
        onChange={(v) => setForm((f) => ({ ...f, action: v as RetentionAction }))}
        options={[
          { value: "trash", label: "Move to trash" },
          { value: "delete", label: "Trash, then permanently delete" },
        ]}
      />
      <Field
        label="After (days)"
        type="number"
        min={1}
        value={form.after_days}
        onChange={(e) => setForm((f) => ({ ...f, after_days: e.target.value }))}
      />
      <Select
        label="Recording kind"
        value={form.applies_to}
        onChange={(v) => setForm((f) => ({ ...f, applies_to: v as RetentionAppliesTo }))}
        options={[
          { value: "all", label: "All" },
          { value: "meetings", label: "Meeting recordings" },
          { value: "non_meetings", label: "Non-meeting recordings" },
        ]}
      />
      </div>
      <div className="admin-row-sub">{actionCopy(form.action)}</div>
      <SaveRow
        onSave={() => onSubmit(form)}
        onCancel={onCancel}
        saving={submitting}
        disabled={form.scope !== "org" && !form.scope_id}
        saveLabel={isEdit ? "Save changes" : "Add policy"}
      />
    </SettingsSection>
  );
}

export default function RetentionPage() {
  const { org } = useOutletContext<AdminOutletContext>();
  const [policies, setPolicies] = useState<RetentionPolicyOut[]>([]);
  const [teams, setTeams] = useState<TeamOut[]>([]);
  const [folders, setFolders] = useState<FolderOut[]>([]);
  const [pending, setPending] = useState<PendingPurgeOut[]>([]);
  const [dryRun, setDryRun] = useState<RetentionDryRunOut | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<RetentionPolicyOut | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [editing, setEditing] = useState<RetentionPolicyOut | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [policiesRes, teamsRes, pendingRes, foldersRes] = await Promise.all([
        api<ListResponse<RetentionPolicyOut>>("GET", `/orgs/${org.id}/retention-policies`),
        api<ListResponse<TeamOut>>("GET", `/orgs/${org.id}/teams`),
        api<ListResponse<PendingPurgeOut>>("GET", `/orgs/${org.id}/retention-policies/pending`),
        listFolders(org.id).catch(() => ({ items: [] as FolderOut[] }) as ListResponse<FolderOut>),
      ]);
      setFolders(foldersRes.items);
      setPolicies(policiesRes.items);
      setTeams(teamsRes.items);
      setPending(pendingRes.items);
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : "Couldn't load retention settings.");
    } finally {
      setLoading(false);
    }
  }, [org.id]);

  useEffect(() => {
    void load();
  }, [load]);

  const refreshPreview = useCallback(async () => {
    setPreviewing(true);
    try {
      const res = await api<RetentionDryRunOut>("POST", `/orgs/${org.id}/retention-policies/dry-run`);
      setDryRun(res);
    } catch {
      // Best-effort — the preview is a convenience, not required to use this page.
    } finally {
      setPreviewing(false);
    }
  }, [org.id]);

  useEffect(() => {
    if (!loading) void refreshPreview();
  }, [loading, refreshPreview]);

  async function onCreate(form: PolicyFormState) {
    setSubmitting(true);
    setFormError(null);
    try {
      await api("POST", `/orgs/${org.id}/retention-policies`, {
        scope: form.scope,
        scope_id: form.scope === "org" ? undefined : form.scope_id,
        action: form.action,
        after_days: Number(form.after_days),
        applies_to: form.applies_to,
      });
      setShowForm(false);
      setCreating(false);
      await load();
      await refreshPreview();
    } catch (err) {
      setFormError(err instanceof ApiFail ? err.message : "Couldn't create that policy.");
    } finally {
      setSubmitting(false);
    }
  }

  async function onUpdate(policy: RetentionPolicyOut, form: PolicyFormState) {
    setSubmitting(true);
    setFormError(null);
    try {
      await api("PATCH", `/orgs/${org.id}/retention-policies/${policy.id}`, {
        action: form.action,
        after_days: Number(form.after_days),
        applies_to: form.applies_to,
      });
      setShowForm(false);
      await load();
      await refreshPreview();
    } catch (err) {
      setFormError(err instanceof ApiFail ? err.message : "Couldn't update that policy.");
    } finally {
      setSubmitting(false);
    }
  }

  async function onDeleteConfirm() {
    if (!deleteConfirm) return;
    const policy = deleteConfirm;
    setDeleteConfirm(null);
    try {
      await api("DELETE", `/orgs/${org.id}/retention-policies/${policy.id}`);
      await load();
      await refreshPreview();
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : "Couldn't delete that policy.");
    }
  }

  return (
    <>
      <header className="page-head">
        <div>
          <h1>Retention</h1>
          <p>
            Automatically trash or permanently delete old recordings. "Trash, then permanently
            delete" moves a recording to trash immediately and purges it 7 days later — owners are
            notified 7 days before the action, and anything on legal hold is skipped.
          </p>
        </div>
      </header>

      {error ? <Notice tone="error">{error}</Notice> : null}

      <div className="admin-row-sub admin-toolbar">
        <span>{dryRunSummaryText(dryRun)}</span>
        <Button variant="ghost" size="sm" onClick={() => void refreshPreview()} disabled={previewing}>
          {previewing ? "Checking…" : "Refresh preview"}
        </Button>
      </div>

      {loading ? (
        <div className="admin-row-sub">Loading…</div>
      ) : (
        <>
          {policies.length === 0 ? (
            <div className="admin-row-sub">
              No retention policies yet. Recordings are kept indefinitely until you add one.
            </div>
          ) : (
            <Card size="flush" className="admin-table-card">
            <table className="admin-table">
              <thead>
                <tr>
                  <th className="gk-overline">Applies to</th>
                  <th className="gk-overline">Action</th>
                  <th className="gk-overline">After</th>
                  <th className="gk-overline">Recording kind</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {policies.map((p) => (
                  <tr key={p.id}>
                    <td>{scopeLabel(p, teams, folders)}</td>
                    <td>
                      <Badge size="sm">{p.action === "trash" ? "Trash" : "Trash + delete"}</Badge>
                    </td>
                    <td>{p.after_days} days</td>
                    <td>{appliesToLabel(p.applies_to)}</td>
                    <td>
                      <div className="row-actions">
                        <Button variant="secondary" size="sm"
                          onClick={() => {
                            setEditing(p);
                            setShowForm(true);
                            setCreating(false);
                          }}
                        >
                          Edit
                        </Button>
                        <Button variant="secondary" size="sm"
                          onClick={() => setDeleteConfirm(p)}
                        >
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </Card>
          )}

          {showForm && editing ? (
            <PolicyForm
              key={editing.id}
              teams={teams}
              folders={folders}
              initial={{
                scope: editing.scope,
                scope_id: editing.scope_id ?? "",
                action: editing.action,
                after_days: String(editing.after_days),
                applies_to: editing.applies_to,
              }}
              isEdit
              submitting={submitting}
              onCancel={() => {
                setShowForm(false);
                setEditing(null);
              }}
              onSubmit={(form) => void onUpdate(editing, form)}
            />
          ) : showForm && creating ? (
            <PolicyForm
              teams={teams}
              folders={folders}
              initial={emptyForm}
              isEdit={false}
              submitting={submitting}
              onCancel={() => {
                setShowForm(false);
                setCreating(false);
              }}
              onSubmit={(form) => void onCreate(form)}
            />
          ) : (
            <div>
              <Button
                variant="secondary"
                icon="plus"
                onClick={() => {
                  setCreating(true);
                  setEditing(null);
                  setShowForm(true);
                  setFormError(null);
                }}
              >
                Add retention policy
              </Button>
            </div>
          )}
          {formError ? <Notice tone="error">{formError}</Notice> : null}

          <SettingsSection
            title="Pending permanent deletion"
            description="Recordings a “trash, then permanently delete” policy has already trashed, waiting out their 7-day grace before they're purged for good."
          >
          {pending.length === 0 ? (
            <div className="admin-row-sub">Nothing is pending permanent deletion.</div>
          ) : (
            <div className="list">
              {pending.map((p) => (
                <div className="list-row" key={p.id}>
                  <div className="list-row__body">
                    <div className="list-row__title">
                      {p.title || "Untitled"}
                      {p.on_legal_hold ? <HoldBadge /> : null}
                    </div>
                    <div className="list-row__sub">
                      Owned by {p.owner.name || p.owner.email} · purges{" "}
                      {new Date(p.purge_after).toLocaleString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          </SettingsSection>
        </>
      )}

      {deleteConfirm ? (
        <ConfirmDialog
          open
          title="Delete retention policy"
          message="Delete this retention policy? Recordings it has already acted on are unaffected."
          confirmLabel="Delete"
          danger
          onClose={() => setDeleteConfirm(null)}
          onConfirm={onDeleteConfirm}
        />
      ) : null}
    </>
  );
}
